import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBox,
  faPlus,
  faEdit,
  faTrash,
  faCopy,
  faSave,
  faTimes,
  faUsers,
  faCommentDots,
  faDatabase,
  faGlobe,
  faVideo,
  faSearch,
  faChartLine,
  faBolt,
  faLock,
  faSpinner,
  faToggleOn,
  faToggleOff,
  faRobot,
  faPalette,
  faChartBar,
  faQuoteLeft,
  faQuestionCircle,
  faBook,
  faUsersCog,
  faCalendar,
  faSync,
  faCreditCard,
  faCheckCircle,
  faDollarSign,
  faEnvelope,
  faExternalLinkAlt,
  faFileInvoice,
  faClock,
  faCheck,
  faExclamationTriangle,
  faFilter,
  faRedo,
  faTimesCircle,
  faLifeRing,
  faMicrophone,
  faTshirt,
  faHeadset,
  faUserTie
} from '@fortawesome/free-solid-svg-icons';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'react-toastify';
import {
  getAllPlans,
  createPlan,
  updatePlan,
  deletePlan,
  duplicatePlan,
  getApiBaseUrl
} from '../../services/api';
import axios from 'axios';

// Plan templates for quick setup
const PLAN_TEMPLATES = {
  starter: {
    name: 'Starter',
    description: 'Perfect for individuals and small businesses just getting started',
    suggested_price: 299,
    limits: {
      max_messages_per_month: 10000,
      seo_pages_per_month: 50,
      max_document_uploads_per_month: 50,
      max_website_recrawls_per_month: 1,
      max_youtube_videos_per_month: 0
    }
  },
  professional: {
    name: 'Professional',
    description: 'For growing businesses with increased usage needs',
    suggested_price: 799,
    limits: {
      max_messages_per_month: 50000,
      seo_pages_per_month: 200,
      max_document_uploads_per_month: 200,
      max_website_recrawls_per_month: 5,
      max_youtube_videos_per_month: 20
    }
  },
  business: {
    name: 'Business',
    description: 'For established businesses with high-volume requirements',
    suggested_price: 1999,
    limits: {
      max_messages_per_month: 200000,
      seo_pages_per_month: 1000,
      max_document_uploads_per_month: 1000,
      max_website_recrawls_per_month: -1,
      max_youtube_videos_per_month: -1
    }
  }
};

const PlanManagementSimple: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [showDefaultTiers, setShowDefaultTiers] = useState(false);
  const [defaultTiers, setDefaultTiers] = useState<any[]>([]);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [showLoadConfirmModal, setShowLoadConfirmModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  // Form state
  const [formData, setFormData] = useState<any>({
    name: '',
    display_name: '',
    description: '',
    tier: 'starter',
    price_monthly_cents: 29900,
    price_yearly_cents: 299000,
    max_messages_per_month: 10000,
    seo_pages_per_month: 50,
    api_calls_per_month: 0,
    // Recurring usage limits
    max_document_uploads_per_month: 50,
    max_website_recrawls_per_month: 1,
    max_youtube_videos_per_month: 0,
    features: {
      chat: { enabled: true },
      theming: { enabled: true },
      analytics: { enabled: true },
      citations: { enabled: true },
      seo_analysis: { enabled: true },
      qa_validation: { enabled: true },
      knowledge_base: { enabled: true },
      youtube_integration: { enabled: false },
      calendar_integration: { enabled: false },
      support_flows: { enabled: false },
      dynamic_prompting: { enabled: true },
      source_preferences: { enabled: true },
      virtual_tryon: { enabled: false },
      priority_support: { enabled: false },
      dedicated_manager: { enabled: false },
      widget_integration: {
        enabled: true,
        javascript_embed: true,
        iframe_embed: true
      }
    },
    is_public: true,
    is_recommended: false,
    sort_order: 1
  });

  useEffect(() => {
    // Check if user is super admin
    const userRole = localStorage.getItem('user_role');
    if (userRole !== 'super') {
      navigate('/consultant/dashboard');
      return;
    }

    fetchPlans();
  }, [navigate]);

  // Lock body scroll when any modal is open
  useEffect(() => {
    if (showModal || showLoadConfirmModal || showBulkDeleteModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal, showLoadConfirmModal, showBulkDeleteModal]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await getAllPlans(true);
      setPlans(response);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingPlan) {
        const updated = await updatePlan(editingPlan.id, formData);
        setPlans(plans.map(p => p.id === editingPlan.id ? updated : p));
        toast.success('Plan updated successfully');
      } else {
        const created = await createPlan(formData);
        setPlans([...plans, created]);
        toast.success('Plan created successfully');
      }
      setShowModal(false);
      setEditingPlan(null);
      resetForm();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (planId: string) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    
    try {
      await deletePlan(planId);
      setPlans(plans.filter(p => p.id !== planId));
      toast.success('Plan deleted successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete plan');
    }
  };

  const handleDuplicate = async (planId: string) => {
    const newName = prompt('Enter a name for the duplicated plan:');
    if (!newName) return;
    
    try {
      const duplicated = await duplicatePlan(planId, newName.toLowerCase().replace(/\s+/g, '_'));
      setPlans([...plans, duplicated]);
      toast.success('Plan duplicated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate plan');
    }
  };

  const handleSyncToStripe = async (planId: string) => {
    if (!window.confirm('Are you sure you want to sync this plan to Stripe? This will create/update the product and prices in your Stripe account.')) {
      return;
    }
    
    try {
      const response = await axios.post(`${getApiBaseUrl()}/plan-management/plans/${planId}/sync-stripe`);
      toast.success('Plan synced to Stripe successfully');
      await fetchPlans(); // Refresh to get updated Stripe IDs
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to sync plan to Stripe');
    }
  };

  const handleSyncAllToStripe = async () => {
    if (!window.confirm('Are you sure you want to sync ALL plans to Stripe? This will create/update products and prices for all plans in your Stripe account.')) {
      return;
    }

    try {
      const response = await axios.post(`${getApiBaseUrl()}/plan-management/sync-all-to-stripe`);
      const data = response.data;

      if (data.success) {
        toast.success(`Successfully synced ${data.synced_count} plans to Stripe`);
      } else {
        toast.warning(`Synced ${data.synced_count} out of ${data.total_plans} plans. Check console for errors.`);
        console.error('Sync errors:', data.errors);
      }

      await fetchPlans(); // Refresh to get updated Stripe IDs
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to sync plans to Stripe');
    }
  };

  const handlePreviewDefaultTiers = async () => {
    setLoadingDefaults(true);
    try {
      const response = await axios.get(`${getApiBaseUrl()}/plan-management/plans/defaults/preview`);
      setDefaultTiers(response.data);
      setShowDefaultTiers(true);
      toast.success('Default tiers loaded for preview');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to preview default tiers');
    } finally {
      setLoadingDefaults(false);
    }
  };

  const confirmLoadDefaultTiers = () => {
    setShowLoadConfirmModal(true);
  };

  const handleLoadDefaultTiers = async () => {
    setShowLoadConfirmModal(false);
    setLoadingDefaults(true);
    try {
      const response = await axios.post(`${getApiBaseUrl()}/plan-management/plans/defaults/load`);
      const data = response.data;

      if (data.created_count > 0) {
        toast.success(data.message);
        await fetchPlans(); // Refresh to show new plans
        setShowDefaultTiers(false);
      } else {
        toast.info(data.message);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load default tiers');
    } finally {
      setLoadingDefaults(false);
    }
  };

  const handleBulkDeleteAllPlans = async () => {
    setShowBulkDeleteModal(false);
    setDeletingAll(true);
    try {
      const response = await axios.delete(`${getApiBaseUrl()}/plan-management/plans/bulk-delete`);
      const data = response.data;

      if (data.deleted_count > 0) {
        toast.success(`${data.message}. Stripe products archived: ${data.stripe_products_archived || 0}`);
        await fetchPlans(); // Refresh to show empty list
      } else {
        toast.info(data.message);
      }

      if (data.warnings && data.warnings.length > 0) {
        data.warnings.forEach((warning: string) => toast.warning(warning));
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete plans';
      toast.error(errorMsg);
    } finally {
      setDeletingAll(false);
    }
  };

  const openModal = (plan?: any) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData(plan);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      description: '',
      tier: 'starter',
      price_monthly_cents: 29900,
      price_yearly_cents: 299000,
      max_messages_per_month: 10000,
      seo_pages_per_month: 50,
      api_calls_per_month: 0,
      features: {
        chat: { enabled: true },
        theming: { enabled: true },
        analytics: { enabled: true },
        citations: { enabled: true },
          seo_analysis: { enabled: true },
        qa_validation: { enabled: true },
        knowledge_base: { enabled: true },
        youtube_integration: { enabled: false },
        calendar_integration: { enabled: false },
        widget_integration: {
          enabled: true,
          javascript_embed: true,
          iframe_embed: true
        }
      },
      is_public: true,
      is_recommended: false,
      sort_order: 1
    });
  };

  const applyTemplate = (templateKey: string) => {
    const template = PLAN_TEMPLATES[templateKey as keyof typeof PLAN_TEMPLATES];
    if (!template) return;
    
    setFormData({
      ...formData,
      name: templateKey,
      display_name: template.name,
      description: template.description,
      tier: templateKey,
      price_monthly_cents: template.suggested_price * 100,
      price_yearly_cents: template.suggested_price * 100 * 10,
      ...template.limits
    });
  };

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(cents / 100);
  };

  const getTierColor = (tier: string) => {
    const colors: any = {
      starter: 'text-blue-600 bg-blue-100',
      professional: 'text-purple-600 bg-purple-100',
      business: 'text-green-600 bg-green-100',
      enterprise: 'text-orange-600 bg-orange-100',
      custom: 'text-gray-600 bg-gray-100'
    };
    return colors[tier] || 'text-gray-600 bg-gray-100';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin h-8 w-8 text-gray-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Default Tiers Section */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faDollarSign} className="text-blue-600" />
              Default Subscription Tiers
            </div>
            <div className="flex gap-2">
              <button
                onClick={handlePreviewDefaultTiers}
                disabled={loadingDefaults}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors text-sm disabled:opacity-50"
              >
                {loadingDefaults ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin h-4 w-4" />
                ) : (
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                )}
                Preview Tiers
              </button>
              <button
                onClick={confirmLoadDefaultTiers}
                disabled={loadingDefaults}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors text-sm disabled:opacity-50"
              >
                {loadingDefaults ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin h-4 w-4" />
                ) : (
                  <FontAwesomeIcon icon={faCheck} />
                )}
                Load Default Tiers
              </button>
              <button
                onClick={() => setShowBulkDeleteModal(true)}
                disabled={loadingDefaults || plans.length === 0}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors text-sm disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faTrash} />
                Delete All Plans
              </button>
            </div>
          </CardTitle>
          <CardDescription>
            Subscription tiers designed for per-client billing with overage-based pricing. Based on $0.028 per message API cost.
          </CardDescription>
        </CardHeader>
        {showDefaultTiers && defaultTiers.length > 0 && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {defaultTiers.map((tier) => (
                <div key={tier.name} className="bg-white rounded-lg p-4 border shadow-sm">
                  <div className="font-semibold text-lg">{tier.display_name}</div>
                  <div className="text-2xl font-bold text-blue-600 mt-2">
                    {formatPrice(tier.price_monthly_cents)}
                    <span className="text-sm text-gray-500">/mo</span>
                  </div>
                  <div className="text-xs text-gray-600 mt-2">
                    {tier.max_messages_per_month} messages/month
                  </div>
                  {tier.overage_message_rate_cents && tier.overage_message_rate_cents > 0 && (
                    <div className="text-xs text-orange-600 mt-1">
                      Overage: {formatPrice(tier.overage_message_rate_cents)}/1000 msgs
                    </div>
                  )}
                  {tier.is_recommended && (
                    <Badge variant="secondary" className="mt-2 text-xs">
                      <FontAwesomeIcon icon={faBolt} className="mr-1" />
                      Recommended
                    </Badge>
                  )}
                  {!tier.is_public && (
                    <Badge variant="secondary" className="mt-2 text-xs bg-gray-200">
                      <FontAwesomeIcon icon={faLock} className="mr-1" />
                      Private
                    </Badge>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 mr-2" />
              <strong>Note:</strong> These tiers are based on actual token costs. Click "Load Default Tiers" to add them to your database.
            </div>
          </CardContent>
        )}
      </Card>

      {/* Actions */}
      <div className="flex justify-end mb-4">
        <div className="flex gap-2">
          <button
            onClick={handleSyncAllToStripe}
            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
            title="Sync all plans to Stripe"
          >
            <FontAwesomeIcon icon={faSync} />
            Sync All to Stripe
          </button>
          <button
            onClick={() => openModal()}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
          >
            <FontAwesomeIcon icon={faPlus} />
            Create New Plan
          </button>
        </div>
      </div>

      {/* Revenue Strategy Card */}
      <Card className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FontAwesomeIcon icon={faChartLine} className="text-purple-600" />
            Revenue Growth Strategy
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <h4 className="font-semibold mb-1">🎯 Value Ladder</h4>
              <p className="text-gray-700">Start users on Starter, show value, upgrade to Professional</p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">💰 Usage Limits</h4>
              <p className="text-gray-700">Messages & SEO pages drive overage revenue</p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">🚀 Premium Features</h4>
              <p className="text-gray-700">YouTube & API access justify higher tiers</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <Card key={plan.id} className="relative hover:shadow-lg transition-shadow">
            <div className={`absolute top-0 left-0 right-0 h-1 ${
              plan.tier === 'starter' ? 'bg-blue-500' :
              plan.tier === 'professional' ? 'bg-purple-500' :
              plan.tier === 'business' ? 'bg-green-500' :
              plan.tier === 'enterprise' ? 'bg-orange-500' : 'bg-gray-500'
            }`} />
            
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-xl">{plan.display_name}</CardTitle>
                  <div className="flex gap-2 mt-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${getTierColor(plan.tier)}`}>
                      {plan.tier}
                    </span>
                    {plan.is_recommended && (
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-600 flex items-center gap-1">
                        <FontAwesomeIcon icon={faBolt} className="h-3 w-3" />
                        Recommended
                      </span>
                    )}
                    {!plan.is_public && (
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600 flex items-center gap-1">
                        <FontAwesomeIcon icon={faLock} className="h-3 w-3" />
                        Private
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => openModal(plan)}
                    className="p-2 hover:bg-gray-100 rounded transition-colors"
                    title="Edit plan"
                  >
                    <FontAwesomeIcon icon={faEdit} className="h-4 w-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => handleDuplicate(plan.id)}
                    className="p-2 hover:bg-gray-100 rounded transition-colors"
                    title="Duplicate plan"
                  >
                    <FontAwesomeIcon icon={faCopy} className="h-4 w-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => handleSyncToStripe(plan.id)}
                    className="p-2 hover:bg-gray-100 rounded transition-colors"
                    title="Sync to Stripe"
                  >
                    <FontAwesomeIcon icon={faSync} className="h-4 w-4 text-blue-600" />
                  </button>
                  <button
                    onClick={() => handleDelete(plan.id)}
                    className="p-2 hover:bg-gray-100 rounded transition-colors"
                    title="Delete plan"
                  >
                    <FontAwesomeIcon icon={faTrash} className="h-4 w-4 text-red-500" />
                  </button>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">{plan.description}</p>
              
              {/* Pricing */}
              <div>
                <div className="text-3xl font-bold">{formatPrice(plan.price_monthly_cents)}</div>
                <div className="text-sm text-gray-500">per month</div>
                {plan.price_yearly_cents && (
                  <div className="text-sm text-green-600 mt-1">
                    <div className="font-medium">{formatPrice(plan.price_yearly_cents)}/year</div>
                    <div>
                      Save {Math.round((1 - (plan.price_yearly_cents / 12) / plan.price_monthly_cents) * 100)}%
                      with yearly billing
                    </div>
                  </div>
                )}
              </div>

              {/* Key Limits */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    <FontAwesomeIcon icon={faCommentDots} className="h-4 w-4 mr-1" />
                    Messages/mo
                  </span>
                  <span className="font-medium">{plan.max_messages_per_month.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    <FontAwesomeIcon icon={faSearch} className="h-4 w-4 mr-1" />
                    SEO Pages/mo
                  </span>
                  <span className="font-medium">{plan.seo_pages_per_month}</span>
                </div>
                {plan.max_youtube_videos_per_month > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      <FontAwesomeIcon icon={faVideo} className="h-4 w-4 mr-1" />
                      YouTube Videos/mo
                    </span>
                    <span className="font-medium">{plan.max_youtube_videos_per_month}</span>
                  </div>
                )}
              </div>

              {/* Active Subscriptions */}
              {plan.active_subscriptions_count !== undefined && (
                <div className="pt-3 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Active subscriptions</span>
                    <Badge variant="secondary">{plan.active_subscriptions_count}</Badge>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-y-auto"
          style={{ zIndex: 9999 }}
          onClick={(e) => {
            // Close modal if clicking on backdrop
            if (e.target === e.currentTarget) {
              setShowModal(false);
              setEditingPlan(null);
              resetForm();
            }
          }}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full my-8 max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">
                {editingPlan ? `Edit Plan: ${editingPlan.display_name}` : 'Create New Plan'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingPlan(null);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              <div className="p-6">
                {/* Tabs */}
                <div className="flex space-x-1 mb-6 border-b">
                  <button
                    onClick={() => setActiveTab('basic')}
                    className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                      activeTab === 'basic' 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Basic Info
                  </button>
                  <button
                    onClick={() => setActiveTab('limits')}
                    className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                      activeTab === 'limits' 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Limits & Features
                  </button>
                  <button
                    onClick={() => setActiveTab('pricing')}
                    className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                      activeTab === 'pricing' 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Pricing
                  </button>
                </div>

                {/* Tab Content */}
                {activeTab === 'basic' && (
                  <div className="space-y-6">
                    {/* Template Selection */}
                    {!editingPlan && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Start from template
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                          {Object.entries(PLAN_TEMPLATES).map(([key, template]) => (
                            <button
                              key={key}
                              onClick={() => applyTemplate(key)}
                              className="p-4 border rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
                            >
                              <div className="font-semibold">{template.name}</div>
                              <div className="text-xs text-gray-500 mt-1">
                                ${template.suggested_price}/mo
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Internal Name
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="starter_plan"
                          disabled={editingPlan}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Display Name
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                          value={formData.display_name}
                          onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                          placeholder="Starter Plan"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Perfect for small businesses..."
                        rows={3}
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tier
                        </label>
                        <select
                          className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                          value={formData.tier}
                          onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
                        >
                          <option value="starter">Starter</option>
                          <option value="professional">Professional</option>
                          <option value="business">Business</option>
                          <option value="enterprise">Enterprise</option>
                          <option value="custom">Custom</option>
                        </select>
                      </div>
                      <div>
                        <label className="flex items-center gap-2 mt-6">
                          <input
                            type="checkbox"
                            checked={formData.is_public}
                            onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                            className="rounded"
                          />
                          <span className="text-sm font-medium text-gray-700">Public</span>
                        </label>
                      </div>
                      <div>
                        <label className="flex items-center gap-2 mt-6">
                          <input
                            type="checkbox"
                            checked={formData.is_recommended}
                            onChange={(e) => setFormData({ ...formData, is_recommended: e.target.checked })}
                            className="rounded"
                          />
                          <span className="text-sm font-medium text-gray-700">Recommended</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'limits' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Core Limits</h3>
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Messages/Month
                          </label>
                          <input
                            type="number"
                            className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                            value={formData.max_messages_per_month}
                            onChange={(e) => setFormData({ ...formData, max_messages_per_month: parseInt(e.target.value) })}
                          />
                          <p className="text-xs text-gray-500 mt-1">Primary billing metric - number of chat messages</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Feature Limits</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            SEO Pages/Month
                          </label>
                          <input
                            type="number"
                            className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                            value={formData.seo_pages_per_month}
                            onChange={(e) => setFormData({ ...formData, seo_pages_per_month: parseInt(e.target.value) })}
                          />
                          <p className="text-xs text-gray-500 mt-1">0 = disabled, -1 = unlimited</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Document Uploads/Month
                          </label>
                          <input
                            type="number"
                            className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                            value={formData.max_document_uploads_per_month || 0}
                            onChange={(e) => setFormData({ ...formData, max_document_uploads_per_month: parseInt(e.target.value) })}
                          />
                          <p className="text-xs text-gray-500 mt-1">Number of documents, -1 = unlimited</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Website Re-crawls/Month
                          </label>
                          <input
                            type="number"
                            className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                            value={formData.max_website_recrawls_per_month || 0}
                            onChange={(e) => setFormData({ ...formData, max_website_recrawls_per_month: parseInt(e.target.value) })}
                          />
                          <p className="text-xs text-gray-500 mt-1">Full site re-crawls, -1 = unlimited</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            YouTube Videos/Month
                          </label>
                          <input
                            type="number"
                            className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                            value={formData.max_youtube_videos_per_month || 0}
                            onChange={(e) => setFormData({ ...formData, max_youtube_videos_per_month: parseInt(e.target.value) })}
                          />
                          <p className="text-xs text-gray-500 mt-1">Number of videos to process, 0 = disabled, -1 = unlimited</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Features</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {/* Core Features */}
                        <div className="space-y-3">
                          <h4 className="font-medium text-sm text-gray-700">Core Features</h4>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.features.chat?.enabled || false}
                              onChange={(e) => setFormData({
                                ...formData,
                                features: {
                                  ...formData.features,
                                  chat: { enabled: e.target.checked }
                                }
                              })}
                              className="rounded"
                            />
                            <FontAwesomeIcon icon={faRobot} className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">Chat AI Assistant</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.features.theming?.enabled || false}
                              onChange={(e) => setFormData({
                                ...formData,
                                features: {
                                  ...formData.features,
                                  theming: { enabled: e.target.checked }
                                }
                              })}
                              className="rounded"
                            />
                            <FontAwesomeIcon icon={faPalette} className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">Custom Theming</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.features.analytics?.enabled || false}
                              onChange={(e) => setFormData({
                                ...formData,
                                features: {
                                  ...formData.features,
                                  analytics: { enabled: e.target.checked }
                                }
                              })}
                              className="rounded"
                            />
                            <FontAwesomeIcon icon={faChartBar} className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">Analytics Dashboard</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.features.citations?.enabled || false}
                              onChange={(e) => setFormData({
                                ...formData,
                                features: {
                                  ...formData.features,
                                  citations: { enabled: e.target.checked }
                                }
                              })}
                              className="rounded"
                            />
                            <FontAwesomeIcon icon={faQuoteLeft} className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">Source Citations</span>
                          </label>
                        </div>

                        {/* Advanced Features */}
                        <div className="space-y-3">
                          <h4 className="font-medium text-sm text-gray-700">Advanced Features</h4>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.features.seo_analysis?.enabled || false}
                              onChange={(e) => setFormData({
                                ...formData,
                                features: {
                                  ...formData.features,
                                  seo_analysis: { enabled: e.target.checked }
                                }
                              })}
                              className="rounded"
                            />
                            <FontAwesomeIcon icon={faSearch} className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">SEO Analysis</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.features.qa_validation?.enabled || false}
                              onChange={(e) => setFormData({
                                ...formData,
                                features: {
                                  ...formData.features,
                                  qa_validation: { enabled: e.target.checked }
                                }
                              })}
                              className="rounded"
                            />
                            <FontAwesomeIcon icon={faQuestionCircle} className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">QA Validation</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.features.knowledge_base?.enabled || false}
                              onChange={(e) => setFormData({
                                ...formData,
                                features: {
                                  ...formData.features,
                                  knowledge_base: { enabled: e.target.checked }
                                }
                              })}
                              className="rounded"
                            />
                            <FontAwesomeIcon icon={faBook} className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">Knowledge Base</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.features.youtube_integration?.enabled || false}
                              onChange={(e) => setFormData({
                                ...formData,
                                features: {
                                  ...formData.features,
                                  youtube_integration: { enabled: e.target.checked }
                                }
                              })}
                              className="rounded"
                            />
                            <FontAwesomeIcon icon={faVideo} className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">YouTube Integration</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.features.calendar_integration?.enabled || false}
                              onChange={(e) => setFormData({
                                ...formData,
                                features: {
                                  ...formData.features,
                                  calendar_integration: { enabled: e.target.checked }
                                }
                              })}
                              className="rounded"
                            />
                            <FontAwesomeIcon icon={faCalendar} className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">Calendar Integration</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.features.support_flows?.enabled || false}
                              onChange={(e) => setFormData({
                                ...formData,
                                features: {
                                  ...formData.features,
                                  support_flows: { enabled: e.target.checked }
                                }
                              })}
                              className="rounded"
                            />
                            <FontAwesomeIcon icon={faLifeRing} className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">Support Flows</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.features.dynamic_prompting?.enabled || false}
                              onChange={(e) => setFormData({
                                ...formData,
                                features: {
                                  ...formData.features,
                                  dynamic_prompting: { enabled: e.target.checked }
                                }
                              })}
                              className="rounded"
                            />
                            <FontAwesomeIcon icon={faEdit} className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">Dynamic Prompting</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.features.source_preferences?.enabled || false}
                              onChange={(e) => setFormData({
                                ...formData,
                                features: {
                                  ...formData.features,
                                  source_preferences: { enabled: e.target.checked }
                                }
                              })}
                              className="rounded"
                            />
                            <FontAwesomeIcon icon={faFilter} className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">Source Preferences</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.features.virtual_tryon?.enabled || false}
                              onChange={(e) => setFormData({
                                ...formData,
                                features: {
                                  ...formData.features,
                                  virtual_tryon: { enabled: e.target.checked }
                                }
                              })}
                              className="rounded"
                            />
                            <FontAwesomeIcon icon={faTshirt} className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">Virtual Try-On (Fashion)</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.features.priority_support?.enabled || false}
                              onChange={(e) => setFormData({
                                ...formData,
                                features: {
                                  ...formData.features,
                                  priority_support: { enabled: e.target.checked }
                                }
                              })}
                              className="rounded"
                            />
                            <FontAwesomeIcon icon={faHeadset} className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">Priority Support</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.features.dedicated_manager?.enabled || false}
                              onChange={(e) => setFormData({
                                ...formData,
                                features: {
                                  ...formData.features,
                                  dedicated_manager: { enabled: e.target.checked }
                                }
                              })}
                              className="rounded"
                            />
                            <FontAwesomeIcon icon={faUserTie} className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">Dedicated Account Manager</span>
                          </label>
                        </div>
                      </div>

                      {/* Widget Integration (Always Enabled) */}
                      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FontAwesomeIcon icon={faGlobe} className="text-blue-600" />
                          <strong className="text-blue-800">Widget Integration</strong>
                          <Badge variant="secondary" className="text-xs">Always Enabled</Badge>
                        </div>
                        <p className="text-sm text-blue-700">
                          All plans include widget integration with JavaScript and iframe embed options.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'pricing' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Monthly Price
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                            $
                          </span>
                          <input
                            type="number"
                            className="w-full pl-8 pr-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                            value={formData.price_monthly_cents / 100}
                            onChange={(e) => setFormData({ ...formData, price_monthly_cents: parseInt(e.target.value) * 100 })}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Yearly Price
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                            $
                          </span>
                          <input
                            type="number"
                            className="w-full pl-8 pr-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                            value={formData.price_yearly_cents / 100}
                            onChange={(e) => setFormData({ ...formData, price_yearly_cents: parseInt(e.target.value) * 100 })}
                          />
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {formData.price_yearly_cents && formData.price_monthly_cents ? (
                            `${Math.round((1 - (formData.price_yearly_cents / 12) / formData.price_monthly_cents) * 100)}% discount`
                          ) : (
                            'Set yearly price'
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Revenue Projection */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold mb-3 text-green-800">Revenue Projection</h4>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <div className="text-gray-600">10 customers</div>
                          <div className="font-semibold text-green-700">
                            {formatPrice(formData.price_monthly_cents * 10)}/mo
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600">50 customers</div>
                          <div className="font-semibold text-green-700">
                            {formatPrice(formData.price_monthly_cents * 50)}/mo
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600">100 customers</div>
                          <div className="font-semibold text-green-700">
                            {formatPrice(formData.price_monthly_cents * 100)}/mo
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingPlan(null);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin h-4 w-4" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="h-4 w-4" />
                    {editingPlan ? 'Update Plan' : 'Create Plan'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load Default Tiers Confirmation Modal */}
      {showLoadConfirmModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          style={{ zIndex: 9999 }}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <FontAwesomeIcon icon={faCheck} className="text-green-600 text-xl" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Load Default Subscription Tiers?
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    This will create 5 new subscription plans in your database:
                  </p>
                  <ul className="text-sm text-gray-700 space-y-1 mb-4">
                    <li className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 w-4 h-4" />
                      Free - $0/month (100 messages)
                    </li>
                    <li className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 w-4 h-4" />
                      Starter - $15/month (500 messages)
                    </li>
                    <li className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 w-4 h-4" />
                      Professional - $49/month (2000 messages)
                    </li>
                    <li className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 w-4 h-4" />
                      Business - $149/month (10000 messages)
                    </li>
                    <li className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 w-4 h-4" />
                      Enterprise - $399/month (25000 messages)
                    </li>
                  </ul>
                  <p className="text-xs text-gray-500">
                    <strong>All features included in all plans!</strong> Pricing based only on message volume ($0.021/msg cost).
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 rounded-b-lg">
              <button
                onClick={() => setShowLoadConfirmModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLoadDefaultTiers}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faCheck} />
                Yes, Load Tiers
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          style={{ zIndex: 9999 }}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 text-xl" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Delete ALL Subscription Plans?
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    <strong className="text-red-600">WARNING:</strong> This will permanently delete:
                  </p>
                  <ul className="text-sm text-gray-700 space-y-2 mb-4 bg-red-50 p-3 rounded border border-red-200">
                    <li className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faTimesCircle} className="text-red-500 w-4 h-4" />
                      All {plans.length} plan(s) from database
                    </li>
                    <li className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faTimesCircle} className="text-red-500 w-4 h-4" />
                      Associated Stripe products (archived)
                    </li>
                    <li className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faTimesCircle} className="text-red-500 w-4 h-4" />
                      All pricing information
                    </li>
                  </ul>
                  <p className="text-xs text-red-600 font-semibold mb-2">
                    ⚠️ This action cannot be undone!
                  </p>
                  <p className="text-xs text-gray-500">
                    Note: This will fail if any active subscriptions exist.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 rounded-b-lg">
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDeleteAllPlans}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faTrash} />
                Yes, Delete ALL Plans
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanManagementSimple;